<template>
    <div class="page" :class="{'surface fill-height': phoneOnly}">
        <v-container :class="{'px-0': phoneOnly}">
            <v-card :flat="phoneOnly" class="px-1 px-lg-3" :class="{'transparent': phoneOnly, 'soft-shadow': pcOnly}">
                <v-card-title class="d-flex flex-wrap align-center" style="row-gap: 8px;">
                    <span class="me-2">Quarterly Report History</span>
                </v-card-title>
                <v-card-subtitle :class="{'pe-3': phoneOnly}">
                    Review the quarterly reports you've submitted. Filter by batch or month/year.
                </v-card-subtitle>

                <v-card-text :class="{'px-0': phoneOnly}">
                    <!-- Filters -->
                    <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px;" :class="{'px-4': phoneOnly}">
                        <v-select
                            v-model="batchFilter"
                            :items="batchOptions"
                            outlined dense hide-details
                            label="Batch"
                            placeholder="All"
                            clearable
                            style="max-width: 160px;"
                            @change="applyFilters"
                        />
                        <v-menu
                            v-model="monthMenu"
                            :close-on-content-click="false"
                            min-width="290px"
                            transition="scale-transition"
                            offset-y
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :value="cycleMonthDisplay"
                                    label="Month/Year"
                                    placeholder="All"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly clearable outlined dense hide-details
                                    style="max-width: 220px;"
                                    v-bind="attrs" v-on="on"
                                    @click:clear="clearMonth"
                                />
                            </template>
                            <v-date-picker
                                v-model="cycleMonth"
                                type="month"
                                no-title
                                @input="onMonthPicked"
                            />
                        </v-menu>
                    </div>

                    <v-progress-circular v-if="loading && !hasData" indeterminate color="primary" class="d-block mx-auto my-6" />

                    <infinity-wrapper v-else-if="hasData" @load="loadMore" key="qr-history">
                        <v-list max-width="850px" class="pa-0 ma-0 transparent">
                            <template v-for="(item, i) in history">
                                <v-list-item :key="item.id" class="my-1" two-line @click="openView(item)">
                                    <v-list-item-avatar tile class="primary me-3 me-lg-6" :size="phoneOnly ? 40 : 45">
                                        <v-icon color="white" style="opacity: 0.85" :small="phoneOnly">mdi-file-document-outline</v-icon>
                                    </v-list-item-avatar>
                                    <v-list-item-content class="py-2">
                                        <v-list-item-title class="font-weight-medium" style="white-space: normal;">
                                            {{ studentName(item) }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle class="details-text" style="white-space: normal;">
                                            {{ courseName(item) }}
                                            <span v-if="item.cycle_month" class="muted--text"> · {{ formatCycleMonth(item.cycle_month) }}</span>
                                            <span v-if="item.batch_id" class="muted--text"> · Batch {{ item.batch_id }}</span>
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action class="d-flex flex-row align-center" style="gap: 6px;" @click.stop>
                                        <v-chip x-small label :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
                                        <v-btn v-if="item.status === 'reversed'" small color="red darken-2" depressed @click.stop="openEdit(item)">
                                            <v-icon left small>mdi-pencil</v-icon>
                                            Edit
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                                <v-divider v-if="i < history.length - 1" :inset="pcOnly" style="opacity: 0.6" :key="'d-' + item.id" />
                            </template>
                        </v-list>
                        <div v-if="loading" class="text-center py-4">
                            <v-progress-circular indeterminate color="primary" size="24" />
                        </div>
                    </infinity-wrapper>

                    <div v-else :class="{'px-4': phoneOnly}">
                        <v-alert type="info" color="blue-grey" text>
                            No reports match the selected filters.
                        </v-alert>
                    </div>
                </v-card-text>
            </v-card>
        </v-container>

        <!-- Read-only view dialog -->
        <v-dialog v-model="viewDialog" max-width="700" scrollable :fullscreen="phoneOnly" :transition="phoneOnly ? 'dialog-bottom-transition' : 'dialog-transition'">
            <v-card v-if="activeReport">
                <v-card-title class="d-flex align-center">
                    <v-icon class="me-2" color="primary">mdi-file-document-outline</v-icon>
                    <div class="flex-grow-1" style="min-width: 0;">
                        <div style="line-height: 1.2;">{{ studentName(activeReport) }}</div>
                        <div class="caption muted--text mt-1">{{ courseName(activeReport) }}</div>
                    </div>
                    <v-btn icon @click="viewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-4">
                    <quarterly-report-view :report="activeReport" />
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Edit & Resubmit dialog (reversed reports) -->
        <quarterly-report-form-dialog
            v-model="editDialog"
            :enrollment="editEnrollmentPayload"
            @submitted="onResubmitted"
        />
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';
import InfinityWrapper from '@/components/default/infinity-wrapper';
import QuarterlyReportView from '@/components/quarterly-report/quarterly-report-view';
import QuarterlyReportFormDialog from '@/components/quarterly-report/quarterly-report-form-dialog';

export default {
    name: 'QuarterlyReportsHistory',
    components: { InfinityWrapper, QuarterlyReportView, QuarterlyReportFormDialog },
    data() {
        return {
            loading: false,
            batchFilter: null,
            cycleMonth: null,         // 'YYYY-MM' from picker
            monthMenu: false,
            viewDialog: false,
            editDialog: false,
            editEnrollmentPayload: null,
            activeReport: null,
            batchOptions: [
                { text: 'Batch 1', value: 1 },
                { text: 'Batch 2', value: 2 },
                { text: 'Batch 3', value: 3 },
            ],
        };
    },
    computed: {
        ...GlobalComputed,
        history() { return this.$store.state.quarterlyReportsModule.history || []; },
        hasData() { return this.history && this.history.length > 0; },
        cycleMonthDisplay() {
            if (!this.cycleMonth) return '';
            const [y, m] = this.cycleMonth.split('-');
            const date = new Date(Number(y), Number(m) - 1, 1);
            return date.toLocaleString(undefined, { month: 'long', year: 'numeric' });
        },
    },
    methods: {
        ...GlobalMethods,
        studentName(item) { return (item.enrollment && item.enrollment.user && item.enrollment.user.name) || ''; },
        courseName(item) { return (item.enrollment && item.enrollment.course && item.enrollment.course.name) || ''; },
        statusLabel(s) {
            return ({ submitted: 'Submitted', ready: 'Ready', released: 'Released', reversed: 'Reversed' })[s] || s;
        },
        statusColor(s) {
            return ({ submitted: 'blue-grey', ready: 'orange darken-1', released: 'success', reversed: 'red darken-2' })[s] || 'grey';
        },
        formatCycleMonth(d) {
            if (!d) return '';
            const date = new Date(d);
            if (Number.isNaN(date.getTime())) return d;
            return date.toLocaleString(undefined, { month: 'short', year: 'numeric' });
        },
        onMonthPicked(val) {
            this.monthMenu = false;
            this.cycleMonth = val;
            this.applyFilters();
        },
        clearMonth() {
            this.cycleMonth = null;
            this.applyFilters();
        },
        applyFilters() {
            this.$store.dispatch('resetQuarterlyHistory');
            this.loadData(true);
        },
        loadData(initialLoad = false) {
            if (this.loading) return;
            this.loading = true;
            const payload = { initialLoad };
            if (this.batchFilter)  payload.batch_id    = this.batchFilter;
            if (this.cycleMonth)   payload.cycle_month = this.cycleMonth + '-01';
            this.$store.dispatch('loadQuarterlyHistory', payload)
                .catch(() => { this.showMsg('Failed to load reports.', 'error'); })
                .finally(() => { this.loading = false; });
        },
        loadMore() { this.loadData(false); },
        openView(item) {
            this.activeReport = item;
            this.viewDialog = true;
        },
        openEdit(item) {
            // Build the same shape the form dialog expects on the Due Report page:
            // an enrollment-like object with a reversed_report payload it can prefill from.
            const enrollment = item.enrollment || {};
            this.editEnrollmentPayload = {
                enrollment_id: enrollment.id,
                user:          enrollment.user || null,
                course:        enrollment.course || null,
                category:      enrollment.course && enrollment.course.category && enrollment.course.category.name,
                reversed_report: {
                    id:                    item.id,
                    cycle_month:           item.cycle_month,
                    level:                 item.level,
                    progress_summary:      item.progress_summary,
                    topics_covered:        item.topics_covered || [],
                    performance_areas:     item.performance_areas || {},
                    areas_for_improvement: item.areas_for_improvement,
                    next_phase_plan:       item.next_phase_plan,
                    teachers_message:      item.teachers_message,
                },
            };
            this.editDialog = true;
        },
        onResubmitted() {
            this.showMsg('Report resubmitted successfully.', 'success');
            this.editEnrollmentPayload = null;
            this.$store.dispatch('resetQuarterlyHistory');
            this.loadData(true);
        },
    },
    created() {
        // Always reset to fresh list — we may be returning after submitting a new report
        // which pushed an entry into the cached history with no batch/cycle filtering applied.
        this.$store.dispatch('resetQuarterlyHistory');
        this.loadData(true);
    },
};
</script>
